/**
 * Training Integration Layer
 * Connects SDK Service to Zustand Store
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useTrainingStore } from '../../store/training.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { TRAINING_VIDEO_MAX_BYTES } from '@repo/contracts';
import { toUploadProgressHandler } from '@/lib/upload/percent-from-axios-progress.js';
import { isUploadAbortError } from '@/lib/upload/is-upload-abort.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class TrainingIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchStats() {
        try {
            const response = await this.service.getStats();
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error), data: null };
        }
    }

    /**
     * Map backend item to frontend format
     */
    _mapToUI(item) {
        if (!item) return null;
        return {
            ...item,
            id: item._id || item.id,
            trainingName: item.title,
            trainingProvider: item.externalInstructor || item.description || "",
            trainingDuration: item.duration,
            startDate: item.scheduledDate,
            endDate: item.deadlineDate,
            numberOfPersonnel: Array.isArray(item.trainedPersons) ? item.trainedPersons.length : 0
        };
    }

    /**
     * Fetch all items and update store
     */
    async fetchTrainings(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useTrainingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return {
                success: false,
                error: i18next.t('common:messages.tenantRequired', {
                    defaultValue: 'Tenant selection is required',
                }),
            };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        const apiKey = key === 'trainingName' ? 'title' : key;
                        acc[`filter_${apiKey}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, uniqueValues } = response;
            const pagination = response.pagination || response.meta || {};
            const uiData = (data || []).map(item => this._mapToUI(item));

            store.setTrainings(uiData);

            const currentPage = Number(pagination.page) || page;
            const itemsPerPage = Number(pagination.pageSize) || pageSize;
            const totalItems = pagination.total != null ? Number(pagination.total) : (data?.length || 0);
            const totalPages = Number(pagination.totalPages) || Math.ceil(totalItems / itemsPerPage) || 1;

            store.setPaginationData({
                currentPage,
                totalPages,
                totalItems,
                itemsPerPage,
                hasNextPage: currentPage < totalPages,
                hasPreviousPage: currentPage > 1,
            });

            if (uniqueValues) {
                store.setUniqueValues({
                    ...uniqueValues,
                    ...(uniqueValues.title && !uniqueValues.trainingName
                        ? { trainingName: uniqueValues.title }
                        : {}),
                });
            }

            return { success: true, data: uiData };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Get by ID
     * @param {string} id
     * @param {{ silent?: boolean }} [opts] - silent skips list loading spinner (edit open)
     */
    async fetchTrainingById(id, opts = {}) {
        const store = useTrainingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        const silent = Boolean(opts.silent);

        if (!silent) store.setLoading(true);

        if (!tenantId) {
            if (!silent) store.setLoading(false);
            return {
                success: false,
                error: i18next.t('common:messages.tenantRequired', {
                    defaultValue: 'Tenant selection is required',
                }),
            };
        }

        try {
            const response = await this.service.getById(id);
            const uiItem = this._mapToUI(response.data);
            if (store.setSelectedTraining) {
                store.setSelectedTraining(uiItem);
            }
            return { success: true, data: uiItem };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (!silent) store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createTraining(data) {
        const store = useTrainingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return {
                success: false,
                error: i18next.t('common:messages.tenantRequired', {
                    defaultValue: 'Tenant selection is required',
                }),
            };
        }

        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('iso27001Training:messages.createSuccess', { defaultValue: 'Training created' }));
            return { success: true, data: this._mapToUI(response.data) };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateTraining(id, data) {
        const store = useTrainingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return {
                success: false,
                error: i18next.t('common:messages.tenantRequired', {
                    defaultValue: 'Tenant selection is required',
                }),
            };
        }

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('iso27001Training:messages.updateSuccess', { defaultValue: 'Training updated' }));
            return { success: true, data: this._mapToUI(response.data) };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteTraining(id) {
        const store = useTrainingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return {
                success: false,
                error: i18next.t('common:messages.tenantRequired', {
                    defaultValue: 'Tenant selection is required',
                }),
            };
        }

        try {
            await this.service.delete(id);
            toast.success(i18next.t('iso27001Training:messages.deleteSuccess', { defaultValue: 'Training deleted' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete items
     */
    async batchDeleteTraining(ids) {
        const store = useTrainingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return {
                success: false,
                error: i18next.t('common:messages.tenantRequired', {
                    defaultValue: 'Tenant selection is required',
                }),
            };
        }

        try {
            const response = await this.service.bulkDelete(ids);
            toast.success(i18next.t('iso27001Training:messages.bulkDeleteSuccess', { count: ids.length, defaultValue: 'Selected trainings deleted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Upload a training video file to durable storage.
     * @param {File|Blob} file
     * @param {Function|{ onProgress?: Function, signal?: AbortSignal }} [onProgressOrOptions]
     */
    async uploadVideo(file, onProgressOrOptions) {
        const onProgress = typeof onProgressOrOptions === 'function'
            ? onProgressOrOptions
            : onProgressOrOptions?.onProgress;
        const signal = onProgressOrOptions?.signal;
        try {
            if (file?.size > TRAINING_VIDEO_MAX_BYTES) {
                toast.error(i18next.t('iso27001Training:form.fields.videoUrl.tooLarge', {
                    defaultValue: 'This video exceeds the 2 GB limit.',
                }));
                return { success: false, error: 'LIMIT_FILE_SIZE', code: 'LIMIT_FILE_SIZE' };
            }
            const response = await this.service.uploadVideo(file, {
                onUploadProgress: toUploadProgressHandler(onProgress),
                signal,
            });
            return { success: true, data: response.data };
        } catch (error) {
            if (isUploadAbortError(error)) {
                return { success: false, aborted: true, error: 'ABORTED' };
            }
            const code = error.code || error.details?.code;
            if (code === 'LIMIT_FILE_SIZE' || error.statusCode === 413) {
                toast.error(i18next.t('iso27001Training:form.fields.videoUrl.tooLarge', {
                    defaultValue: 'This video exceeds the 2 GB limit.',
                }));
                return { success: false, error: 'LIMIT_FILE_SIZE', code: 'LIMIT_FILE_SIZE' };
            }
            const message = this._getErrorMessage(error);
            toast.error(
                i18next.t('iso27001Training:form.fields.videoUrl.uploadError', {
                    defaultValue: 'Video upload failed',
                }),
            );
            return { success: false, error: message, code };
        }
    }

    /**
     * Remove an uploaded video that is not yet linked to a Training document.
     * @param {string} storageKeyOrUrl
     */
    async discardUploadedVideo(storageKeyOrUrl) {
        if (!storageKeyOrUrl) return { success: true };
        try {
            await this.service.discardUploadedVideo({
                storageKey: storageKeyOrUrl,
                url: storageKeyOrUrl,
            });
            return { success: true };
        } catch (error) {
            if (error.statusCode === 409 || error.code === 'VIDEO_IN_USE') {
                return { success: false, error: 'VIDEO_IN_USE' };
            }
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, { notFoundDefault: 'Training not found' });
    }
}
